import React from "react";
import "./App.css";
import Signup from "./components/auth/Signup";
import Reset from "./components/auth/Reset";

import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
  Navigate
} from "react-router-dom";

import { notification, Result, Button } from "antd";
import moment from "moment";

import { useSelector } from "react-redux";
import {
  getAgent,
  getUser,
} from "./redux/transactions/UntappedSlice";
import LandingPage from "./components/morideHome/LandingPage";
import ApplicationForm from "./components/loanProcess/ApplicationForm";
import AgentDashboard from "./components/loanAgent/AgentDashboard";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const user_info = useSelector(getUser);
  const [hasShownNotification, setHasShownNotification] = React.useState(false);

  // Check if user is logged in and token is valid
  const isLoggedIn = React.useMemo(() => {
    if (!user_info) return false;
    
    const accessTokenExpiry = moment(user_info.access_token_expiry);
    const currentTime = moment();
    return accessTokenExpiry.isAfter(currentTime);
  }, [user_info]);

  React.useEffect(() => {
    if (!isLoggedIn && !hasShownNotification) {
      const message = !user_info 
        ? 'Authentication Required' 
        : 'Session Expired';
      
      const description = !user_info
        ? 'Please log in to access this page'
        : 'Your session has expired. Please log in again';

      api.warning({
        message,
        description,
        placement: 'topRight',
        duration: 4,
      });
      
      setHasShownNotification(true);
    }
  }, [isLoggedIn, api, user_info, hasShownNotification]);

  if (!isLoggedIn) {
    return (
      <>
        {contextHolder}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          padding: '20px'
        }}>
          <Result
            status="403"
            title="Authentication Required"
            subTitle="You need to be logged in to access this page."
            extra={
              <Button type="primary" onClick={() => navigate('/')}>
                Go to Home
              </Button>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      {children}
    </>
  );
};

// Protected Agent Route Component
const ProtectedAgentRoute = ({ children }) => {
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const loggedInAgent = useSelector(getAgent);
  const [hasShownNotification, setHasShownNotification] = React.useState(false);

  const isLoggedInAgent = loggedInAgent !== null;

  React.useEffect(() => {
    if (!isLoggedInAgent && !hasShownNotification) {
      api.warning({
        message: 'Agent Authentication Required',
        description: 'Please log in as an agent to access this page',
        placement: 'topRight',
        duration: 4,
      });
      
      setHasShownNotification(true);
    }
  }, [isLoggedInAgent, api, hasShownNotification]);

  if (!isLoggedInAgent) {
    return (
      <>
        {contextHolder}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          padding: '20px'
        }}>
          <Result
            status="403"
            title="Agent Authentication Required"
            subTitle="You need to be logged in as an agent to access this page."
            extra={
              <Button type="primary" onClick={() => navigate('/')}>
                Go to Home
              </Button>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      {children}
    </>
  );
};

function App() {
  return (
    <div>
      <Router>
        <div className="justify-center">
          <Routes>
            {/* Public Routes */}
            <Route exact path="/" element={<LandingPage />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/sign-up" element={<Signup />} />
            <Route path="/jali-application-form" element={<ApplicationForm />} />

            {/* Protected Agent Routes */}
            <Route
              path="/jali-application-agent"
              element={
                <ProtectedAgentRoute>
                  <AgentDashboard />
                </ProtectedAgentRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </div>
  );
}

export default App;